import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";

// A remote image can fail for reasons that have nothing to do with the page:
// a deleted object, an expired download token, or -- as happened here -- the
// project's billing being switched off, which makes Cloud Storage answer 402
// for every object. Without a fallback the browser draws its broken-image
// glyph and spills the alt text across the card, which looks like the site
// itself is broken.
function SafeImage({ src, alt, aspectRatio, onClick, sx, fallbackText }) {
  const [failed, setFailed] = useState(false);

  // A new src deserves a fresh attempt, or one broken image would keep its
  // failed state after the list is refreshed.
  useEffect(() => setFailed(false), [src]);

  const frame = {
    width: "100%",
    aspectRatio,
    display: "block",
    ...sx,
  };

  if (!src || failed) {
    return (
      <Box
        sx={{
          ...frame,
          display: "grid",
          placeItems: "center",
          bgcolor: ({ palette: { brand } }) => brand.mist,
          color: "#7b809a",
          textAlign: "center",
          px: 2,
        }}
      >
        <Box>
          <BrokenImageIcon sx={{ fontSize: 30, opacity: 0.6 }} />
          <Typography sx={{ fontSize: "0.72rem", mt: 0.5 }}>{fallbackText}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      onClick={onClick}
      sx={{
        ...frame,
        objectFit: "cover",
        cursor: onClick ? "zoom-in" : "default",
      }}
    />
  );
}

SafeImage.defaultProps = {
  aspectRatio: "3 / 4",
  onClick: undefined,
  sx: {},
  fallbackText: "Image unavailable",
};

SafeImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  aspectRatio: PropTypes.string,
  onClick: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  sx: PropTypes.object,
  fallbackText: PropTypes.string,
};

export default SafeImage;
